<template>
  <Teleport to="body">
    <Transition name="action-feedback">
      <div v-if="show" class="action-feedback-overlay" role="status" aria-live="polite">
        <div
          class="action-feedback-pill"
          :class="`action-feedback-pill--${type}`"
        >
          <span class="action-feedback-icon-wrap">
            <v-icon :icon="icon" class="action-feedback-icon" />
          </span>
          <span class="action-feedback-text">{{ text }}</span>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
defineProps<{
  show: boolean;
  icon: string;
  text: string;
  type?: 'success' | 'info' | 'error';
}>();
</script>

<style lang="scss">
.action-feedback-overlay {
  position: fixed;
  top: calc(var(--safe-area-top, 0px) + 24px);
  left: 50%;
  transform: translateX(-50%);
  z-index: var(--z-toast);
  pointer-events: none;
  max-width: calc(100vw - 32px);
}

.action-feedback-pill {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px 12px 14px;
  border-radius: 999px;
  background: rgba(26, 24, 21, 0.78);
  backdrop-filter: blur(20px) saturate(140%);
  -webkit-backdrop-filter: blur(20px) saturate(140%);
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow:
    0 12px 32px rgba(0, 0, 0, 0.45),
    0 0 0 1px rgba(255, 255, 255, 0.03) inset;
  max-width: 100%;

  &--success {
    border-color: rgba(22, 163, 74, 0.35);
    box-shadow:
      0 12px 32px rgba(0, 0, 0, 0.45),
      0 0 24px rgba(22, 163, 74, 0.18),
      0 0 0 1px rgba(255, 255, 255, 0.03) inset;
  }

  &--error {
    border-color: rgba(220, 38, 38, 0.35);
    box-shadow:
      0 12px 32px rgba(0, 0, 0, 0.45),
      0 0 24px rgba(220, 38, 38, 0.18),
      0 0 0 1px rgba(255, 255, 255, 0.03) inset;
  }
}

.action-feedback-icon-wrap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.06);
  flex-shrink: 0;

  .action-feedback-pill--success & {
    background: rgba(22, 163, 74, 0.18);
  }

  .action-feedback-pill--error & {
    background: rgba(220, 38, 38, 0.18);
  }
}

.action-feedback-icon {
  font-size: 18px !important;
  color: rgba(255, 255, 255, 0.85);

  .action-feedback-pill--success & {
    color: rgb(74, 222, 128);
  }

  .action-feedback-pill--error & {
    color: rgb(248, 113, 113);
  }
}

.action-feedback-text {
  font-family: var(--font-body), -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
  color: rgba(255, 255, 255, 0.95);
  letter-spacing: 0.01em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.action-feedback-enter-active {
  animation: feedback-in 0.32s cubic-bezier(0.16, 1, 0.3, 1);
}

.action-feedback-leave-active {
  animation: feedback-out 0.2s ease-in forwards;
}

@keyframes feedback-in {
  0% {
    opacity: 0;
    transform: translate(-50%, -16px) scale(0.92);
  }
  100% {
    opacity: 1;
    transform: translate(-50%, 0) scale(1);
  }
}

@keyframes feedback-out {
  0% {
    opacity: 1;
    transform: translate(-50%, 0) scale(1);
  }
  100% {
    opacity: 0;
    transform: translate(-50%, -12px) scale(0.96);
  }
}

@media (prefers-reduced-motion: reduce) {
  .action-feedback-enter-active,
  .action-feedback-leave-active {
    animation: none;
  }
}
</style>
